import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const DISMISS_STORAGE_KEY = 'temp_profile_banner_dismissed';

// 화면 전환(대화 목록 ↔ 대화방) 시 마운트/언마운트 중첩으로 인한 0px 오작동 방지용 활성 배너 추적 Set
const activeBanners = new Set();

function syncTempBannerHeight() {
  if (typeof document === 'undefined') return;
  if (activeBanners.size === 0) {
    document.documentElement.style.setProperty('--temp-banner-height', '0px');
    document.documentElement.style.setProperty('--temp-banner-gap', '6px');
  } else {
    const bannerElements = Array.from(activeBanners);
    const activeEl = bannerElements[bannerElements.length - 1];
    if (activeEl && activeEl.offsetHeight) {
      document.documentElement.style.setProperty('--temp-banner-height', `${activeEl.offsetHeight}px`);
      document.documentElement.style.setProperty('--temp-banner-gap', '18px');
    }
  }
}

/**
 * 임시 프로필 안내 카드 (TempProfileBanner)
 * - 둥근 캡슐형 카드 + 2줄 구조 (텍스트 영역 내 첫째 줄/둘째 줄 모두 가운데 정렬)
 * - 좌측 info 아이콘: 18px 고정 컨테이너 + absolute 분리 (28px 크기, 우측 11px 이동, 세로 중앙 정렬)
 *   -> 아이콘 확대 및 위치 이동 시에도 텍스트 영역의 위치와 중심축은 1px도 밀리지 않음
 * - name_set 값이 명확히 false일 때만 렌더 (미도착 null/undefined 제외)
 * - 카드 본체 클릭 시 /profile 이동
 * - X 버튼 클릭 시 stopPropagation 후 sessionStorage에 저장하여 이번 세션 동안 숨김
 */
export default function TempProfileBanner({ nameSet }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const bannerRef = useRef(null);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(DISMISS_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });

  const shouldRender = nameSet === false && !dismissed;

  useEffect(() => {
    if (!shouldRender || !bannerRef.current) return;

    const el = bannerRef.current;
    activeBanners.add(el);
    syncTempBannerHeight();

    const observer = new ResizeObserver(() => {
      syncTempBannerHeight();
    });
    observer.observe(el);

    return () => {
      observer.disconnect();
      activeBanners.delete(el);
      syncTempBannerHeight();
    };
  }, [shouldRender]);

  // [깜빡임 방지 및 노출 조건] name_set 값이 명확히 false일 때만 렌더 (미도착 null/undefined는 렌더 차단)
  if (!shouldRender) {
    return null;
  }

  const handleBannerClick = () => {
    navigate('/profile');
  };

  const handleCloseClick = (e) => {
    e.stopPropagation();
    setDismissed(true);
    try {
      sessionStorage.setItem(DISMISS_STORAGE_KEY, '1');
    } catch {
      // sessionStorage 접근 불가 환경 대응
    }
  };

  return (
    <div
      ref={bannerRef}
      role="region"
      aria-label={t('temp_profile_banner.message')}
      onClick={handleBannerClick}
      className="flex-shrink-0 mx-3 my-2 bg-primary/10 border border-primary/15 rounded-xl px-3.5 py-2.5 flex items-start justify-between cursor-pointer select-none transition-colors hover:bg-primary/15"
    >
      {/* [A] 텍스트 영역: 고정 폭 아이콘 컨테이너 + 텍스트 가운데 정렬 블록 */}
      <div className="flex items-center gap-2.5 min-w-0 flex-1 pr-1.5">
        {/* 아이콘 고정 컨테이너: 폭 18px(flex-shrink-0)로 고정되어 내부 아이콘 확대/이동이 텍스트 영역에 영향 주지 않음 */}
        <div className="relative w-[18px] h-[18px] flex-shrink-0 flex items-center justify-center">
          <span
            className="material-symbols-outlined text-[28px] leading-none text-primary opacity-80 select-none absolute left-[11px] top-1/2 -translate-y-1/2 pointer-events-none"
            aria-hidden="true"
          >
            info
          </span>
        </div>

        {/* 텍스트 2줄 블록 (아이콘 레이아웃 분리로 중심축 완전 고정) */}
        <div className="flex flex-col items-center text-center min-w-0 flex-1">
          <span className="text-on-surface text-[12px] font-normal leading-snug text-center">
            {t('temp_profile_banner.message')}
          </span>
          <span className="text-primary text-[12px] underline font-semibold mt-1 text-center leading-snug">
            {t('temp_profile_banner.action')}
          </span>
        </div>
      </div>

      {/* [B] 닫기(X) 버튼 영역 (카드 오른쪽 위 안쪽에 독립 배치) */}
      <button
        type="button"
        onClick={handleCloseClick}
        aria-label={t('temp_profile_banner.close_aria')}
        className="w-7 h-7 -mr-1 -mt-0.5 flex items-center justify-center flex-shrink-0 rounded-full text-on-surface-variant/70 hover:text-on-surface hover:bg-primary/10 transition-colors cursor-pointer"
      >
        <span className="material-symbols-outlined text-[15px] leading-none" aria-hidden="true">
          close
        </span>
      </button>
    </div>
  );
}
